import React from "react"
import { Button as ButtonPrimitive } from "@base-ui/react/button"
import { cva } from "class-variance-authority"
import { cn } from "../utils"
import { LoadingSpinner } from "../loading-spinner"
import {
  buttonBaseConfig,
  resolveButtonSize,
  useButtonTelemetry,
  type ButtonTelemetryProps,
  type ButtonVariantProps
} from "../button-base"

// =============================================================================
// Props
// =============================================================================
export type ButtonProps = {
  className?: string
  /**
   * Renders a leading `LoadingSpinner` and disables the button. An explicit
   * `disabled` prop takes precedence, so `disabled={false}` keeps a loading
   * button interactive.
   */
  loading?: boolean
} & Omit<ButtonPrimitive.Props, "className"> &
  ButtonVariantProps &
  ButtonTelemetryProps

// =============================================================================
// Styles
// =============================================================================
export const buttonStyles = cva(
  cn(buttonBaseConfig.base, "disabled:cursor-not-allowed"),
  {
    ...buttonBaseConfig,
    variants: {
      ...buttonBaseConfig.variants,
      variant: {
        primary: cn(
          buttonBaseConfig.variants.variant.primary,
          "disabled:bg-gray-100  disabled:text-gray-600 disabled:[&_svg]:text-gray-600"
        ),
        brand: cn(
          buttonBaseConfig.variants.variant.brand,
          "disabled:bg-gray-100 disabled:text-gray-600 disabled:[&_svg]:text-gray-600"
        ),
        secondary: cn(
          buttonBaseConfig.variants.variant.secondary,
          "disabled:bg-gray-100 disabled:text-gray-600 disabled:[&_svg]:text-gray-600"
        ),
        outlined: cn(
          buttonBaseConfig.variants.variant.outlined,
          "disabled:inset-ring-gray-50 disabled:bg-white disabled:text-gray-600 disabled:[&_svg]:text-gray-600"
        ),
        tertiary: cn(
          buttonBaseConfig.variants.variant.tertiary,
          "disabled:bg-transparent disabled:text-gray-600 disabled:[&_svg]:text-gray-600"
        ),
        unstyled: cn(buttonBaseConfig.variants.variant.unstyled)
      }
    }
  }
)

// =============================================================================
// Component
// =============================================================================
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      className,
      variant = "primary",
      size,
      telemetry,
      loading = false,
      disabled,
      children,
      onClick,
      ...props
    },
    ref
  ) {
    const handleClick = useButtonTelemetry({ telemetry, children, onClick })

    return (
      <ButtonPrimitive
        className={cn(
          buttonStyles({
            variant,
            size: resolveButtonSize(variant, size),
            className
          })
        )}
        data-slot="button"
        ref={ref}
        onClick={handleClick}
        disabled={disabled ?? loading}
        {...props}
      >
        {/*
          Keep the spinner out of the children's sibling list to prevent React
          from crashing during browser translation.
        */}
        <span className="contents">
          {loading && <LoadingSpinner data-icon="inline-start" />}
        </span>
        {children}
      </ButtonPrimitive>
    )
  }
)

Button.displayName = "Button"
